import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { useTheme } from '@mui/material';
import { Button } from '@components/index';
import { ProfileModal } from '@pages/customer/common/modals';
import { fonts } from '@theme/fontsCustomer';
import { BriefcaseIcon } from '@icons/BriefcaseIcon';
import { CalendarStartIcon } from '@icons/CalendarStartIcon';
import { ClockIcon } from '@icons/ClockIcon';
import { DescriptionIcon } from '@icons/DescriptionIcon';
import { DottedBorderIcon } from '@icons/DottedBorderIcon';
import { FlagIcon } from '@icons/FlagIcon';
import { FolderIcon } from '@icons/FolderIcon';
import { GroupIcon } from '@icons/GroupIcon';
import { ManagerIcon } from '@icons/ManagerIcon';
import { TitleIcon } from '@icons/TitleIcon';
import ToolsIcon from '@icons/ToolsIcon';
import { dateFormat } from '@utils/dateUtil';
import type { SubscriptionModel } from '@models/SubscriptionModel';
import type { SoftwareCategoryListModel } from '@models/softwareModels/SoftwareCategoryListModel';

interface Props {
  subscription: SubscriptionModel;
  productCategory?: SoftwareCategoryListModel;
  onClickLogin: () => void;
}

export const SubscriptionInfo = ({ subscription, productCategory, onClickLogin }: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [ownerProfileAnchorEl, setOwnerProfileAnchorEl] = useState<HTMLElement | null>(null);

  return (
    <>
      <Container>
        <Title>{t('Subscrib_Detail_Overview_09')}</Title>
        <DetailWrapper>
          <ValuesSection>
            <InfoRow>
              <InfoName>
                <FolderIcon width={20} height={20} color={theme.colors['ic-gray-light']} />
                {t('Subscrib_Detail_UseRequest_06')}
              </InfoName>
              <InfoValue>{productCategory?.name}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoName>
                <TitleIcon width={20} height={20} color={theme.colors['ic-gray-light']} />
                {t('Subscrib_Detail_UseRequest_07')}
              </InfoName>
              <InfoValue>{subscription?.software.name}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoName>
                <FlagIcon width={20} height={20} color={theme.colors['ic-gray-light']} />
                {t('Subscrib_Main_12')}
              </InfoName>
              <InfoValue>{subscription?.plan.planName}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoName>
                <ClockIcon width={20} height={20} color={theme.colors['ic-gray-light']} />
                {t('Subscrib_Edit_01')}
              </InfoName>
              <InfoValue>
                {subscription?.commitmentPeriod} / {subscription?.paymentPeriod}
              </InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoName>
                <CalendarStartIcon width={20} height={20} color={theme.colors['ic-gray-light']} />
                {t('Subscrib_Detail_Overview_13')}
              </InfoName>
              <PeriodInfoBox>
                <InfoValue>
                  {dateFormat(subscription?.startDate, '-', t('DateFormat_YMD'))} ~{' '}
                  {dateFormat(subscription?.endDate, '-', t('DateFormat_YMD'))}
                </InfoValue>
                <Timezone>{`(${subscription?.timeZone})`}</Timezone>
              </PeriodInfoBox>
            </InfoRow>
            <InfoRow>
              <InfoName>
                <DescriptionIcon width={20} height={20} color={theme.colors['ic-gray-light']} />
                {t('Acc_Main_22')}
              </InfoName>
              <InfoValue>{subscription?.isExternalSubscription ? t('Subscrib_Main_19') : '-'}</InfoValue>
            </InfoRow>
          </ValuesSection>
          <DottedBorderIcon margin="24px 0" />
          <ValuesSection>
            <InfoRow>
              <InfoName>
                <ManagerIcon width={20} height={20} color={theme.colors['ic-gray-light']} />
                {t('Subscrib_Edit_08')}
              </InfoName>
              <InfoValue>
                {subscription?.owner.name ? (
                  <OwnerNameButton
                    type="button"
                    onClick={event => setOwnerProfileAnchorEl(event.currentTarget)}
                    className={ownerProfileAnchorEl ? 'clicked' : ''}
                  >
                    {subscription?.owner.name}
                  </OwnerNameButton>
                ) : (
                  '-'
                )}
              </InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoName>
                <GroupIcon width={20} height={20} color={theme.colors['ic-gray-light']} />
                {t('Subscrib_Main_16')}
              </InfoName>
              <InfoValue>{subscription?.userGroupName}</InfoValue>
            </InfoRow>
          </ValuesSection>
          <DottedBorderIcon margin="24px 0" />
          <ValuesSection>
            <InfoRow>
              <InfoName>
                <BriefcaseIcon width={20} height={20} color={theme.colors['ic-gray-light']} />
                {t('Subscrib_Detail_Overview_14')}
              </InfoName>
              <InfoValue>{subscription?.salesManager.name ?? '-'}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoName>
                <ToolsIcon width={20} height={20} color={theme.colors['ic-gray-light']} />
                {t('Subscrib_Detail_Overview_15')}
              </InfoName>
              <InfoValue>{subscription?.technicalSupportManager.name ?? '-'}</InfoValue>
            </InfoRow>
          </ValuesSection>
        </DetailWrapper>
        {!subscription?.connection?.connectionId && (
          <Footer>
            <Button size="large" variant="contain" fullWidth onClick={onClickLogin}>
              {t('Subscrib_Connect_Create_02', { name: subscription?.software.name })}
            </Button>
          </Footer>
        )}
      </Container>
      <ProfileModal
        open={!!ownerProfileAnchorEl}
        anchorEl={ownerProfileAnchorEl}
        onClose={() => setOwnerProfileAnchorEl(null)}
        userId={subscription ? subscription.owner.id : ''}
      />
    </>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const Title = styled.strong`
  ${fonts.Headline7};
  color: ${({ theme: { colors } }) => colors['text-gray-main']};
  margin-bottom: 12px;
`;

const DetailWrapper = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid ${({ theme: { colors } }) => colors['border-gray-lighter']};
  border-radius: 5px;
  width: 100%;
  padding: 28px 24px;
  box-shadow: 0px 1px 0px 0px rgba(0, 0, 0, 0.02), 0px 0px 3px 0px rgba(0, 0, 0, 0.07);
`;

const ValuesSection = styled.div`
  display: flex;
  flex-direction: column;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 32px;
  &:last-of-type {
    margin-bottom: 0;
  }
`;

const InfoName = styled.span`
  display: flex;
  flex-direction: row;
  gap: 12px;
  ${fonts.Body2};
  color: ${({ theme: { colors } }) => colors['text-gray-sub-dark']};
`;

const InfoValue = styled.span`
  ${fonts.Headline8};
  color: ${({ theme: { colors } }) => colors['text-gray-main']};
`;

const PeriodInfoBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;

const Footer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 720px;
  height: 80px;
  background-color: ${({ theme: { colors } }) => colors['bg-gray-light-m']};
  border: 1px solid ${({ theme: { colors } }) => colors['border-gray-lighter']};
  border-radius: 5px;
  padding: 0px 24px;
`;

const Timezone = styled.small`
  ${fonts.Body4}
  color: ${({ theme: { colors } }) => colors['text-gray-light']};
`;

const OwnerNameButton = styled.button`
  all: unset;
  cursor: pointer;
  color: ${({ theme: { colors } }) => colors['text-blue-light']};
  ${fonts.Underline3}

  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
  &.clicked {
    text-decoration: underline;
  }
`;
